import React, { useRef, useEffect, useState } from 'react';
import './_TransactionTabsHeader.sass';
import IconWallet from '../../assets/icons/IconWallet';
import HeaderIconButton from '@pano-project/components/HeaderButtons/HeaderIconButton';
import { useAppSelector } from 'state/store-hooks';
import { shallowEqual } from 'react-redux';
import { useCashierHeaderEvents } from '@pano-project/hooks/useCashierHeaderEvents';
import { useBootHeaderSettings } from '@pano-project/components/CashierHeader/useBootHeaderSettings';
import { debounce } from "utils/helper-functions/debounce";
interface Props {
  transactionType: string;
  isAmountFirstLayout?: boolean;
  lang: (key: string) => string;
  className?: string;
  activeTab?: string;
  onTabClick?: (tab: string) => void;
  withPending?: boolean;
  balance?: string;
  balancePrefix?: React.ReactNode;
  balanceSuffix?: React.ReactNode;
  balanceTabClassName?: string;
  onBack?: () => void;
}

const TransactionTabsHeader: React.FC<Props> = (
  {
    transactionType,
    isAmountFirstLayout = true,
    lang,
    className = '',
    activeTab,
    onTabClick,
    withPending = false,
    balance,
    balancePrefix,
    balanceSuffix,
    balanceTabClassName = '',
    onBack,
  }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const backButtonRef = useRef<HTMLButtonElement | null>(null);
  const depositTabRef = useRef<HTMLDivElement | null>(null);
  const withdrawTabRef = useRef<HTMLDivElement | null>(null);
  const balanceRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const [isTwoRowsHeader, setIsTwoRowsHeader] = useState(false);

  const { showBackButton, showCloseButton, showAnyHeaderButton } = useBootHeaderSettings();
  const { baseCurrency } = useAppSelector((state) => ({
    baseCurrency: state.profileInfo?.base_currency,
  }), shallowEqual);

  const { notify, notifyClose } = useCashierHeaderEvents();
  
  const isActive = (tab: string): boolean =>
    activeTab === undefined ? true : activeTab === tab;

  const handleKeyDown = (tab: string) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTabClick?.(tab);
    }
  };

  const headerClass = [
    'trx-tabs',
    withPending ? 'trx-tabs--with-pending' : '',
    className,
  ].filter(Boolean).join(' ');

  const balanceTabClass = [
    'trx-tabs__tab',
    'trx-tabs__tab--balance',
    balanceTabClassName,
  ].filter(Boolean).join(' ');

  const handleCloseButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    notifyClose();
  };

  useEffect(() => {
    if (!showAnyHeaderButton) return;

    const containerElement = containerRef?.current;
    const backButtonElement = backButtonRef?.current;
    const depositTabElement = depositTabRef?.current;
    const withdrawTabElement = withdrawTabRef?.current;
    const balanceElement = balanceRef?.current;
    const closeButtonElement = closeButtonRef?.current;

    let cancelled = false;

    const checkElementsWidth = debounce(() => {
      if (cancelled) return;
      const containerWidth = containerElement?.offsetWidth || 0;
      const backButtonWidth = backButtonElement?.offsetWidth|| 0;
      const depositTabWidth = depositTabElement?.offsetWidth || 0;
      const withdrawTabWidth = withdrawTabElement?.offsetWidth || 0;
      const balanceWidth = balanceElement?.offsetWidth || 0;
      const closeButtonWidth = closeButtonElement?.offsetWidth || 0;

      const contentWidth = backButtonWidth + (depositTabWidth ? depositTabWidth + 12 : depositTabWidth) + (withdrawTabWidth ? withdrawTabWidth + 12 : withdrawTabWidth) + balanceWidth + closeButtonWidth;

      setIsTwoRowsHeader(!!containerWidth && containerWidth < (contentWidth + 32));
    }, 50);

    const resizeObserver = new ResizeObserver(() => {
      checkElementsWidth();
    });

    containerElement && resizeObserver.observe(containerElement);
    backButtonElement && resizeObserver.observe(backButtonElement);
    depositTabElement && resizeObserver.observe(depositTabElement);
    withdrawTabElement && resizeObserver.observe(withdrawTabElement);
    balanceElement && resizeObserver.observe(balanceElement);
    closeButtonElement && resizeObserver.observe(closeButtonElement);

    return () => {
      cancelled = true;
      resizeObserver.disconnect();
    };
  }, [isTwoRowsHeader]);

  const handleBackButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onBack) {
      onBack();
      return;
    }
    notify('cashier_back_button');
  };

  const renderBackButton = () => (
    <HeaderIconButton
      variant='back'
      buttonRef={backButtonRef}
      onClick={handleBackButtonClick}
      className='trx-tabs__button'
    />
  )

  const renderCloseButton = () => (
    <HeaderIconButton
      variant='close'
      buttonRef={closeButtonRef}
      onClick={handleCloseButtonClick}
      className='trx-tabs__button'
    />
  )

  return (
    <div className={headerClass}>
      {isTwoRowsHeader && (
        <div className="trx-tabs__buttons-wrapper">
          {showBackButton && renderBackButton()}
          {showCloseButton && renderCloseButton()}
        </div>
      )}
      <div className="trx-tabs__list" ref={containerRef} >
        {showBackButton && !isTwoRowsHeader && renderBackButton()}
        {transactionType === 'pay-in' && (
          <div
            ref={depositTabRef}
            className={`trx-tabs__tab${isActive('deposit') ? ' _active' : ''}`}
            {...(onTabClick && {
              onClick: () => onTabClick('deposit'),
              role: 'button',
              tabIndex: 0,
              onKeyDown: handleKeyDown('deposit'),
            })}
          >
            {lang('deposit')}
          </div>
        )}
        {transactionType === 'pay-out' && (
          <div
            ref={withdrawTabRef}
            className={`trx-tabs__tab${isActive('withdrawal') ? ' _active' : ''}`}
            {...(onTabClick && {
              onClick: () => onTabClick('withdrawal'),
              role: 'button',
              tabIndex: 0,
              onKeyDown: handleKeyDown('withdrawal'),
            })}
          >
            {lang('withdraw')}
          </div>
        )}
        <div className={balanceTabClass} ref={balanceRef}>
          <span className="trx-tabs__base-currency">{baseCurrency}</span>
          {balancePrefix}

            {balance && (
              <div className={'balance-block'} {...(onTabClick && balance && {
                onClick: () => onTabClick('balance'),
                role: 'button',
                tabIndex: 0,
                onKeyDown: handleKeyDown('balance'),
              })}
              >
                <IconWallet color={isAmountFirstLayout ? 'var(--neutral_7)' : undefined}/>

                <span>{balance}</span>
              </div>
            )}

          {balanceSuffix}
        </div>
        {showCloseButton && !isTwoRowsHeader && renderCloseButton()}
      </div>
    </div>
  );
};

export default TransactionTabsHeader;
